import { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

/**
 * Standalone function to calculate funds data consistently
 * This is separated so it can be used by both the hook and the action creators
 */
export const calculateFundsData = (availableFunds, positionMetrics = {}) => {
  // Default empty object if no funds data available
  if (!availableFunds) return {};
  
  // Convert to a processed object if needed
  const formattedFunds = typeof availableFunds === 'object' && !Array.isArray(availableFunds)
    ? availableFunds
    : Object.entries(availableFunds).reduce((acc, [key, value]) => {
        acc[String(key)] = Number(value);
        return acc;
      }, {});
  
  if(Object.keys(formattedFunds).length === 0) return {};

  // Extract values with fallbacks to 0
  // Map WebSocket data fields to our calculation fields
  const openingCash = parseFloat(formattedFunds["opening_balance"] || formattedFunds["opening_cash"] || 0);
  const payinValue = parseFloat(formattedFunds["pay_in"] || 0);
  const depositValue = parseFloat(formattedFunds["deposit"] || 0);
  const payOutValue = parseFloat(formattedFunds["pay_out"] || 0);
  const withdrawValue = parseFloat(formattedFunds["withdraw"] || 0);
  const blockedMarginValue = parseFloat(formattedFunds["margin_used"] || formattedFunds["blocked_margin"] || 0);
  
  // Get realized P&L from position metrics or formatted funds
  const positionRealizedPnl = parseFloat(positionMetrics?.realizedPnL || 0);
  const formattedRealizedPnl = parseFloat(formattedFunds["realized_mtm"] || 0);
  
  // Use position metrics if available, otherwise use formatted funds
  const realizedPnlValue = positionRealizedPnl || formattedRealizedPnl;
  
  // Get unrealized P&L from position metrics
  const unrealizedPnlValue = parseFloat(positionMetrics?.unrealizedPnL || 0);
  
  // ALWAYS calculate total P&L as the exact sum of realized and unrealized
  const totalPnlValue = parseFloat((unrealizedPnlValue).toFixed(2));
  
  const credit = parseFloat(formattedFunds["adhoc"] || 0);
  
  // Calculate funds values
  const tradingBalance = Number(credit) + Number(openingCash) + Number(depositValue) + Number(payinValue) + Number(payOutValue) + Number(withdrawValue) + Number(realizedPnlValue);
  const availableCash = tradingBalance - blockedMarginValue + unrealizedPnlValue;
  const equity = tradingBalance + unrealizedPnlValue;
  
  // Return an object with all calculated values
  return {
    rawData: formattedFunds,
    tradingBalance: tradingBalance.toFixed(2),
    availableCash: availableCash.toFixed(2),
    unrealizedPnl: unrealizedPnlValue.toFixed(2),
    totalPnl: totalPnlValue.toFixed(2),
    realizedPnl: 0,
    blockedMargin: blockedMarginValue.toFixed(2),
    equity: equity.toFixed(2),
    credit: credit.toFixed(2)
  };
};

/**
 * Custom hook to calculate and provide global funds data
 * Can be used in any component to access processed funds information
 */
const useFundsCalculator = () => {
  const dispatch = useDispatch();
  
  // Get all relevant data from Redux store
  const availableFunds = useSelector((state) => state.funds.fundsViewV2);
  const positionMetrics = useSelector(state => state.portfolio.positionMetrics || {});
  
  // Memorize the calculated funds data
  const calculatedFunds = useMemo(() => {
    return calculateFundsData(availableFunds, positionMetrics);
  }, [availableFunds, positionMetrics]);
  
  // Update global state with calculated funds data
  useEffect(() => {
    if (Object.keys(calculatedFunds).length > 0) {
      dispatch({
        type: 'SET_CALCULATED_FUNDS',
        payload: calculatedFunds
      });
    }
  }, [calculatedFunds, dispatch]);
  
  // Return the calculated data for direct use in components
  return calculatedFunds;
};

export default useFundsCalculator; 